import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { CloudDownload, HelpOutline, InfoOutlined } from '@material-ui/icons';
import { IconButton } from '@roc/ui';
import { observer } from 'mobx-react';
import { ResolutionStore } from '../../stores';
import { Resolution } from '../../types/resolution';
import { ResolutionContent } from './resolutionContent';

// Design-system values of the new resolution screens.
export const RESOLUTION_COLORS = {
  text: '#16161a',
  muted: '#686c8a',
  border: 'rgba(46, 98, 255, 0.3)',
  infoBackground: 'rgba(46, 98, 255, 0.05)',
  infoText: 'rgb(0, 30, 136)',
  flagBackground: 'rgba(255, 46, 91, 0.2)',
  flagText: 'rgb(100, 0, 27)',
  pendingBackground: 'rgba(246, 165, 44, 0.2)',
  pendingText: 'rgb(120, 75, 0)',
  pendingDot: 'rgb(246, 165, 44)',
  acceptedBackground: 'rgba(46, 160, 90, 0.2)',
  acceptedText: 'rgb(0, 70, 30)',
  acceptedDot: 'rgb(46, 160, 90)',
  brand: '#041c2c',
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    color: RESOLUTION_COLORS.text,
    fontFamily: theme.typography.fontFamily,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  title: {
    margin: 0,
    fontFamily: 'inherit',
    fontSize: 30,
    lineHeight: '34px',
    fontWeight: 700,
    wordBreak: 'break-word',
  },
  help: {
    marginLeft: 6,
    fontSize: 18,
    verticalAlign: 'middle',
    color: RESOLUTION_COLORS.muted,
    cursor: 'help',
  },
  subtitle: {
    fontSize: 16,
    lineHeight: '24px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: 4,
    height: 28,
    padding: '0 12px',
    borderRadius: 9999,
    fontSize: 12,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  pending: {
    backgroundColor: RESOLUTION_COLORS.pendingBackground,
    color: RESOLUTION_COLORS.pendingText,
    '& $dot': { backgroundColor: RESOLUTION_COLORS.pendingDot },
  },
  accepted: {
    backgroundColor: RESOLUTION_COLORS.acceptedBackground,
    color: RESOLUTION_COLORS.acceptedText,
    '& $dot': { backgroundColor: RESOLUTION_COLORS.acceptedDot },
  },
  alert: {
    backgroundColor: RESOLUTION_COLORS.infoBackground,
    color: RESOLUTION_COLORS.infoText,
    borderRadius: 24,
    padding: 16,
  },
  alertTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    lineHeight: '24px',
  },
  alertDescription: {
    paddingLeft: 24,
    fontSize: 14,
    lineHeight: '20px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sectionTitle: {
    margin: 0,
    fontFamily: 'inherit',
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 700,
  },
  hint: {
    margin: 0,
    fontSize: 14,
    lineHeight: '20px',
    color: RESOLUTION_COLORS.muted,
  },
  table: {
    border: `1px solid ${RESOLUTION_COLORS.border}`,
    borderRadius: 16,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 64,
    padding: '4px 16px',
    fontSize: 14,
    lineHeight: '20px',
  },
  label: {
    width: '55%',
    flexShrink: 0,
    paddingRight: 16,
  },
  value: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
  flag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 28,
    padding: '0 12px',
    borderRadius: 9999,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    backgroundColor: RESOLUTION_COLORS.flagBackground,
    color: RESOLUTION_COLORS.flagText,
  },
}));

const STATUS_BADGES: Record<string, { label: string; className: 'pending' | 'accepted' }> = {
  PENDING: { label: 'Resolution needed', className: 'pending' },
  ACCEPTED: { label: 'Accepted', className: 'accepted' },
};

export type ResolutionSummaryProps = {
  resolution: Resolution;
  resolutionStore: ResolutionStore;
  content: ResolutionContent;
  showComment?: boolean;
};

export const ResolutionSummary = observer(({ resolution, resolutionStore, content, showComment = false }: ResolutionSummaryProps) => {
  const classes = useStyles();
  const badge = STATUS_BADGES[resolution?.status];
  const canDownload = resolutionStore.canDownloadFile || resolutionStore.canDownloadFileFromInfoScreen;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box minWidth={0}>
          <h3 className={classes.title}>
            {content.title}
            {resolutionStore.tooltTipTitle && (
              <Tooltip placement="right" title={<p style={{ fontSize: '12px' }}>{resolutionStore.tooltTipTitle}</p>}>
                <HelpOutline className={classes.help} />
              </Tooltip>
            )}
          </h3>
          <Typography component="div" className={classes.subtitle}>
            {content.subtitle}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {badge && (
            <span className={`${classes.badge} ${classes[badge.className]}`}>
              <span className={classes.dot} />
              {badge.label}
            </span>
          )}
          {canDownload && (
            <IconButton
              type="submit"
              color="primary"
              testId="download"
              onClick={() => (resolutionStore.canDownloadFile ? resolutionStore.downloadDocument() : resolutionStore.downloadDocumentByPath())}
            >
              <CloudDownload />
            </IconButton>
          )}
        </Box>
      </Box>

      {content.ask && (
        <Box className={classes.alert} role="alert">
          <Box className={classes.alertTitle}>
            <InfoOutlined style={{ fontSize: 16 }} />
            Why we're asking
          </Box>
          <Box className={classes.alertDescription}>{content.ask}</Box>
        </Box>
      )}

      {content.rows.length > 0 && (
        <Box className={classes.section}>
          <h4 className={classes.sectionTitle}>{content.tableTitle}</h4>
          <Box className={classes.table}>
            {content.rows.map((row, index) => (
              <Box className={classes.row} key={`${row.label}-${index}`}>
                <Box className={classes.label}>{row.label}</Box>
                <Box className={classes.value}>
                  <span>{row.value || '—'}</span>
                  {row.flag && <span className={classes.flag}>{row.flag}</span>}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {showComment && (
        <Box>
          <Typography component="span" color="textSecondary">
            Comment:{' '}
          </Typography>
          <strong>{resolution?.comment}</strong>
        </Box>
      )}
    </Box>
  );
});
